import React, { useEffect, useState } from 'react';
import { KeyTakeaway } from '../../data/checklistData';
import PlaybookTakeawayCard from './PlaybookTakeawayCard';

interface PlaybookTakeawaysProps {
  takeaways: KeyTakeaway[];
  title: string;
  onTakeawayClick: (sectionId?: string) => void;
}

// Column count must match the CSS grid breakpoints (GeoPlaybook.scss `playbook-takeaways__list`):
// 3 columns by default, 2 below 1300px, 1 below 768px. Splitting the takeaways into exactly that many
// columns keeps the numeric (column-major) reading order at every width, and puts the short last
// column — and its filler — on the right.
const getColumnCount = (): number => {
  if (typeof window === 'undefined') return 3;
  if (window.matchMedia('(max-width: 768px)').matches) return 1;
  if (window.matchMedia('(max-width: 1300px)').matches) return 2;
  return 3;
};

const PlaybookTakeaways: React.FC<PlaybookTakeawaysProps> = ({
  takeaways,
  title,
  onTakeawayClick,
}) => {
  const [columnCount, setColumnCount] = useState(getColumnCount);

  useEffect(() => {
    const update = () => setColumnCount(getColumnCount());
    const mqs = [
      window.matchMedia('(max-width: 768px)'),
      window.matchMedia('(max-width: 1300px)'),
    ];
    mqs.forEach((mq) => mq.addEventListener('change', update));
    return () => mqs.forEach((mq) => mq.removeEventListener('change', update));
  }, []);

  const columnSize = Math.max(1, Math.ceil(takeaways.length / columnCount));
  const columns: KeyTakeaway[][] = [];
  for (let i = 0; i < takeaways.length; i += columnSize) {
    columns.push(takeaways.slice(i, i + columnSize));
  }

  return (
    <div className="stats__panel">
      <div className="page-box__title page-box__title--flex">{title}</div>
      <div className="stats-box playbook-takeaways">
        <div className="stats-box__content playbook-takeaways__content">
          <div className="playbook-takeaways__list">
            {columns.map((column, colIdx) => (
              <div key={colIdx} className="playbook-takeaways__column">
                {column.map((kt, idx) => (
                  <PlaybookTakeawayCard
                    key={kt.titleKey}
                    takeaway={kt}
                    index={colIdx * columnSize + idx}
                    onClick={onTakeawayClick}
                  />
                ))}
                {/* Decorative sparkle filling the last (short) column's empty space. The responsive
                    split keeps it in the rightmost column; when columns divide evenly (e.g. 1 column)
                    no column is short, so it isn't rendered. */}
                {colIdx === columns.length - 1 && column.length < columnSize && (
                  <div className="playbook-takeaways__filler">
                    <div className="no-results__icon">
                      <span className="no-results__spark no-results__spark--1" />
                      <span className="no-results__spark no-results__spark--2" />
                      <span className="no-results__spark no-results__spark--3" />
                      <span className="no-results__spark no-results__spark--4" />
                      <div className="no-results__circle">
                        <span className="ico ico--ai-overview no-results__glyph" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybookTakeaways;
